export const PLAIN_APP_ID = "liveChatApp_01JQY91AYX87RNAKQS2694SAZ0";

/**
 * The anonymous join funnel uses the support mailbox; the creator portal
 * uses its native inbox. Neither route should show the Plain launcher. The
 * guest upload page is someone outside NewForm, often on a phone, who has
 * one button to press and no NewForm account to be helped with.
 */
export const HIDDEN_PATH_PREFIXES = ["/join", "/one", "/share/upload"];

/**
 * Routes whose primary action sits bottom-right, where the launcher floats:
 * on the NewForm One review page it covered the Approve button. Patterns,
 * because the client slug sits in the middle of the path.
 */
const HIDDEN_PATH_PATTERNS = [/^\/c\/[^/]+\/newform-one\/review(\/|$)/];

/**
 * Key of the Text thread field in Plain (Settings → Thread fields) that names
 * the creator program a thread is about. The field has to exist in Plain
 * under exactly this key for the value to show up.
 */
export const PLAIN_PROGRAM_THREAD_FIELD_KEY = "program";

export interface PlainCustomerDetails {
  email?: string;
  /** Server-side HMAC of the email; without it Plain treats the email as unverified. */
  emailHash?: string;
  fullName?: string;
  /** Our own id for the customer, so Plain lookups can go by id rather than email. */
  externalId?: string;
}

export interface PlainThreadField {
  key: string;
  type: "STRING";
  stringValue: string;
}

export interface PlainThreadDetails {
  labelTypeIds?: string[];
  tenantIdentifier?: { externalId: string };
  threadFields?: PlainThreadField[];
}

/** The creator program a portal visitor is currently looking at. */
export interface PlainProgramContext {
  name: string;
  /** Present only once the backend has confirmed the tenant exists in Plain. */
  tenantExternalId?: string;
  labelTypeIds?: string[];
}

export interface PlainCustomer {
  email: string;
  emailHash?: string;
  fullName?: string;
  externalId?: string;
  program?: PlainProgramContext;
}

export function isHiddenPath(pathname: string | null): boolean {
  if (!pathname) return false;
  return (
    HIDDEN_PATH_PREFIXES.some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
    ) || HIDDEN_PATH_PATTERNS.some((pattern) => pattern.test(pathname))
  );
}

/** `{}` clears the identity: the widget would otherwise keep attributing chats to whoever signed out. */
export function toCustomerDetails(
  customer: PlainCustomer | undefined,
): PlainCustomerDetails {
  if (!customer) return {};
  return {
    email: customer.email,
    emailHash: customer.emailHash,
    fullName: customer.fullName,
    externalId: customer.externalId,
  };
}

/**
 * What every new thread should carry. A tenant is only sent when the backend
 * has upserted it — an unknown tenant identifier would fail thread creation
 * and leave the creator unable to chat at all. `{}` clears a previous
 * program: Plain.update replaces top-level fields rather than merging them.
 */
export function buildThreadDetails(
  program: PlainProgramContext | undefined,
): PlainThreadDetails {
  if (!program) return {};
  const details: PlainThreadDetails = {
    threadFields: [
      {
        key: PLAIN_PROGRAM_THREAD_FIELD_KEY,
        type: "STRING",
        stringValue: program.name,
      },
    ],
  };
  if (program.tenantExternalId) {
    details.tenantIdentifier = { externalId: program.tenantExternalId };
  }
  if (program.labelTypeIds && program.labelTypeIds.length > 0) {
    details.labelTypeIds = program.labelTypeIds;
  }
  return details;
}
